/* eslint-disable max-lines */
import { useEffect, useState } from 'react'
import { v4 } from 'uuid'

import { Company, SequenceTaskTemplateInput, TaskTemplate, Trade } from '@types'

import addTaskTemplateIcon from '@assets/images/add-task-icon.svg'

import { ToolTip } from '@components/ToolTip'
import { DragAndDropContext, DraggableItem, DropZoneWithOver } from '@components/DragAndDrop'

import { TaskTemplateThumb } from '../TaskTemplateThumb'
import { PopUpAddTaskTemplate } from '../PopUpAddTaskTemplate'
import { PopUpEditTaskTemplate } from '../PopUpEditTaskTemplate'

import {
  Container,
  DropDownLibraryContainer,
  TableContainer,
  TBody,
  Table,
  Th,
  Thead,
  Tr,
  Td,
  TdContent,
  AddTaskTemplateButton,
  AddTaskTemplateImg,
} from './SequenceTemplatePlanning.styled'
import { DropDownLibrary } from '../DropDownLibrary'

export type SequenceTaskTemplatePlanningType = SequenceTaskTemplateInput & {
  id: string
  name: string
  description: string
  duration: number
  workforce: number
  trade: Trade
  company?: Company
}

export type SequenceTaskTemplateNullType = {
  startDay: number
}

interface Props {
  sequenceTaskTemplates: SequenceTaskTemplatePlanningType[]
  setSequenceTaskTemplates: (tasks: SequenceTaskTemplatePlanningType[]) => void
}

const DEFAULT_NUMBER_OF_DAY = 31
const DEFAULT_NUMBER_OF_ROW = 15

export const SequenceTemplatePlanning = (props: Props): JSX.Element => {
  const { sequenceTaskTemplates, setSequenceTaskTemplates } = props

  const NUMBER_OF_DAY = sequenceTaskTemplates.reduce(
    (acc, t) => (acc < t.startDay + Math.ceil(t.duration) ? t.startDay + Math.ceil(t.duration) : acc),
    0,
  )
  const [days, setDays] = useState<number[]>([...Array(DEFAULT_NUMBER_OF_DAY)].map((_, index) => index))

  const NUMBER_OF_ROW = sequenceTaskTemplates.sort((t1, t2) => (t1.startRow > t2.startRow ? -1 : 1))[0]?.startRow || 0
  const [rows, setRows] = useState<number[]>([...Array(NUMBER_OF_ROW)].map((_, index) => index))

  useEffect(() => {
    setDays(
      [...Array(NUMBER_OF_DAY < DEFAULT_NUMBER_OF_DAY ? DEFAULT_NUMBER_OF_DAY : NUMBER_OF_DAY + 1)].map(
        (_, index) => index,
      ),
    )
    setRows(
      [...Array(NUMBER_OF_ROW < DEFAULT_NUMBER_OF_ROW ? DEFAULT_NUMBER_OF_ROW : NUMBER_OF_ROW)].map(
        (_, index) => index,
      ),
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sequenceTaskTemplates])

  const matrix: (SequenceTaskTemplatePlanningType | SequenceTaskTemplateNullType)[][] = []

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = []

    const filteredTaskTemplates = sequenceTaskTemplates
      .filter((t) => t.startRow == rowIndex)
      .sort((t1, t2) => (t1.startDay < t2.startDay ? -1 : 1))

    for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
      const task = filteredTaskTemplates.find((t) => t.startDay == dayIndex)
      if (task) {
        row.push(task)

        dayIndex += Math.ceil(task.duration) - 1
      } else {
        row.push({
          startDay: dayIndex,
        })
      }
    }

    matrix.push(row)
  }

  const getNextRowIndex = (
    rowIndex: number,
    dayIndex: number,
    task: TaskTemplate | SequenceTaskTemplatePlanningType,
  ): number => {
    const taskStartIndex = dayIndex
    const taskEndIndexIndex = dayIndex + Math.ceil(task.duration) - 1

    const filteredTaskTemplates = sequenceTaskTemplates
      .filter((st) => st.id != task.id)
      .filter((t) => t.startRow == rowIndex)
      .filter(
        (t) =>
          (t.startDay >= taskStartIndex && t.startDay <= taskEndIndexIndex) ||
          (t.startDay + Math.ceil(t.duration) - 1 >= taskStartIndex &&
            t.startDay + Math.ceil(t.duration) - 1 <= taskEndIndexIndex) ||
          (t.startDay <= taskStartIndex && t.startDay + Math.ceil(t.duration) - 1 >= taskEndIndexIndex),
      )

    if (filteredTaskTemplates.length != 0) {
      return getNextRowIndex(rowIndex + 1, dayIndex, task)
    }

    return rowIndex
  }

  const onAddTaskTemplate = (
    rowIndex: number,
    dayIndex: number,
    task: TaskTemplate | SequenceTaskTemplatePlanningType,
  ) => {
    const filteredSequenceTaskTemplates = sequenceTaskTemplates.filter((st) => st.id != task.id)

    const availableRowIndex = getNextRowIndex(rowIndex, dayIndex, task)

    const newTaskTemplate: SequenceTaskTemplatePlanningType = {
      id: v4(),
      name: task.name,
      description: task.description,
      startDay: dayIndex,
      startRow: availableRowIndex,
      duration: task.duration,
      workforce: task.workforce,
      tradeKey: task.trade.key,
      trade: task.trade,
      companyId: task.company?.id,
      company: task.company,
    }

    setSequenceTaskTemplates([...filteredSequenceTaskTemplates, newTaskTemplate])
  }

  const onDeleteTaskTemplate = (task: SequenceTaskTemplatePlanningType) => {
    const filteredSequenceTaskTemplates = sequenceTaskTemplates.filter((st) => st.id != task.id)
    setSequenceTaskTemplates(filteredSequenceTaskTemplates)
  }

  const onUpdateTaskTemplate = (task: SequenceTaskTemplatePlanningType) => {
    const filteredSequenceTaskTemplates = sequenceTaskTemplates.filter((st) => st.id != task.id)
    setSequenceTaskTemplates([...filteredSequenceTaskTemplates, task])
  }

  const [addingTaskTemplate, setAddingTaskTemplate] = useState<{ startRow: number; startDay: number } | undefined>(
    undefined,
  )
  const [editingTaskTemplate, setEditingTaskTemplate] = useState<SequenceTaskTemplatePlanningType | undefined>(
    undefined,
  )

  return (
    <DragAndDropContext
      renderItem={({ item }) => {
        const { task, sequenceTaskTemplate } = item.data

        if (task?.__typename == 'TaskTemplate') {
          const taskTemplate = task as TaskTemplate
          return <TaskTemplateThumb task={taskTemplate} style={{ opacity: 0.6 }} />
        }

        const taskTemplate = sequenceTaskTemplate as SequenceTaskTemplatePlanningType
        return (
          <TaskTemplateThumb
            task={taskTemplate}
            view="calendar"
            style={{
              opacity: 0.6,
              width: Math.ceil(taskTemplate.duration) * 88 - 16,
            }}
          />
        )
      }}
      onDragEnd={({ item, zone }) => {
        const { task, sequenceTaskTemplate } = item.data
        const taskTemplate = sequenceTaskTemplate as SequenceTaskTemplatePlanningType

        if (!zone?.data) {
          return
        }

        const { startRow, startDay } = zone.data

        if (task?.__typename == 'TaskTemplate') {
          onAddTaskTemplate(startRow, startDay, task)
        } else if (taskTemplate) {
          onAddTaskTemplate(startRow, startDay, taskTemplate)
        }
      }}
    >
      <Container>
        <DropDownLibraryContainer>
          <DropDownLibrary library="task" />
        </DropDownLibraryContainer>

        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                {days.map((day) => (
                  <Th key={day}>{`J+${day == 0 ? '' : `${day}`}`}</Th>
                ))}
              </Tr>
            </Thead>
            <TBody>
              {matrix.map((row, rowIndex) => (
                <Tr key={rowIndex}>
                  {row.map((st) => {
                    if (Object.hasOwn(st, 'startRow')) {
                      const sequenceTaskTemplate = st as SequenceTaskTemplatePlanningType
                      return (
                        <Td key={st.startDay} colSpan={Math.ceil(sequenceTaskTemplate.duration)}>
                          <TdContent>
                            <ToolTip
                              position="left"
                              WrappedComponent={() => (
                                <DraggableItem id={sequenceTaskTemplate.id} data={{ sequenceTaskTemplate }}>
                                  <TaskTemplateThumb
                                    task={sequenceTaskTemplate}
                                    view="calendar"
                                    style={{
                                      width: Math.ceil(sequenceTaskTemplate.duration) * 88 - 16,
                                    }}
                                  />
                                </DraggableItem>
                              )}
                              WrappedChildren={({ hide }) => (
                                <TaskTemplateThumb
                                  task={sequenceTaskTemplate}
                                  view="tooltip"
                                  onClickEdit={() => {
                                    setEditingTaskTemplate(sequenceTaskTemplate)

                                    hide()
                                  }}
                                  onClickDelete={() => {
                                    onDeleteTaskTemplate(sequenceTaskTemplate)
                                  }}
                                />
                              )}
                            />
                          </TdContent>
                        </Td>
                      )
                    }

                    return (
                      <Td key={st.startDay}>
                        <TdContent>
                          <DropZoneWithOver
                            id={`${rowIndex}-${st.startDay}`}
                            data={{ startRow: rowIndex, startDay: st.startDay }}
                          >
                            {({ isOver }) => (
                              <AddTaskTemplateButton
                                isActive={isOver}
                                onClick={() => {
                                  setAddingTaskTemplate({ startRow: rowIndex, startDay: st.startDay })
                                }}
                              >
                                <AddTaskTemplateImg src={addTaskTemplateIcon} />
                              </AddTaskTemplateButton>
                            )}
                          </DropZoneWithOver>
                        </TdContent>
                      </Td>
                    )
                  })}
                </Tr>
              ))}
            </TBody>
          </Table>
          {addingTaskTemplate && (
            <PopUpAddTaskTemplate
              startRow={addingTaskTemplate.startRow}
              startDay={addingTaskTemplate.startDay}
              onSubmit={(taskTemplate) => {
                onAddTaskTemplate(addingTaskTemplate.startRow, addingTaskTemplate.startDay, taskTemplate)

                setAddingTaskTemplate(undefined)
              }}
              onClose={() => setAddingTaskTemplate(undefined)}
            />
          )}

          {editingTaskTemplate && (
            <PopUpEditTaskTemplate
              task={editingTaskTemplate}
              onSubmit={(taskTemplate) => {
                onUpdateTaskTemplate(taskTemplate)

                setEditingTaskTemplate(undefined)
              }}
              onClose={() => setEditingTaskTemplate(undefined)}
            />
          )}
        </TableContainer>
      </Container>
    </DragAndDropContext>
  )
}
